import { useState } from 'react';
import { DesignToolbar } from './components/DesignToolbar';
import { DesignCanvas, DesignElement } from './components/DesignCanvas';
import { OrderForm } from './components/OrderForm';

const shirtColors = [
  {
    name: 'White',
    value: '#FFFFFF',
    image: 'https://public.readdy.ai/ai/img_res/862b85e8f93be97eb5f4d13060a5080b.jpg',
  },
  {
    name: 'Gray',
    value: '#9CA3AF',
    image: 'https://readdy.ai/api/search-image?query=professional%20apparel%20product%20photography%20of%20a%20solid%20heather%20gray%20medium%20grey%20crew%20neck%20t-shirt%20displayed%20on%20an%20invisible%20mannequin%20ghost%20mannequin%20front%20view%20perfectly%20centered%20on%20pure%20white%20background%20studio%20lighting%20sharp%20crisp%20edges%20realistic%20fabric%20texture%20natural%20cotton%20weave%20visible%20collar%20seams%20sleeves%20smooth%20wrinkle-free%20surface%20high%20resolution%20ecommerce%20mockup%20no%20text%20no%20graphics%20no%20logos&width=1200&height=1500&seq=hires-gray-shirt-v9&orientation=portrait',
  },
  {
    name: 'Cream',
    value: '#F5F0E8',
    image: 'https://readdy.ai/api/search-image?query=professional%20apparel%20product%20photography%20of%20a%20solid%20cream%20ivory%20off-white%20warm%20beige%20crew%20neck%20t-shirt%20displayed%20on%20an%20invisible%20mannequin%20ghost%20mannequin%20front%20view%20perfectly%20centered%20on%20light%20gray%20background%20studio%20lighting%20sharp%20crisp%20edges%20realistic%20fabric%20texture%20natural%20cotton%20weave%20visible%20collar%20seams%20sleeves%20smooth%20wrinkle-free%20surface%20high%20resolution%20ecommerce%20mockup%20no%20text%20no%20graphics%20no%20logos&width=1200&height=1500&seq=hires-cream-shirt-v9&orientation=portrait',
  },
  {
    name: 'Camo',
    value: '#6B7C4A',
    image: 'https://static.readdy.ai/image/82ad8c75777da265d85bbd063cac2e03/e318ee8a4bbbff4a4277efc3b58ead3c.webp',
  },
];

const printLocations = ['Front Center', 'Back Center', 'Left Chest', 'Right Chest'];

// Pricing logic
const BASE_PRICE = 29.99;
const PRINT_LOCATION_PRICES: Record<string, number> = {
  'Front Center': 0,
  'Back Center': 0,
  'Left Chest': 0,
  'Right Chest': 0,
};
const DESIGN_COMPLEXITY_PRICE = 2.50; // per additional element after first

export default function CustomizePage() {
  const [selectedColor, setSelectedColor] = useState(shirtColors[0]);
  const [printLocation, setPrintLocation] = useState('Front Center');
  const [elements, setElements] = useState<DesignElement[]>([]);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState('M');

  // Calculate dynamic price
  const calculatePrice = () => {
    let price = BASE_PRICE;
    
    // Add print location cost
    price += PRINT_LOCATION_PRICES[printLocation] || 0;
    
    // Add design complexity cost (charge for each element after the first)
    if (elements.length > 1) {
      price += (elements.length - 1) * DESIGN_COMPLEXITY_PRICE;
    }
    
    return price;
  };

  const unitPrice = calculatePrice();
  const totalPrice = unitPrice * quantity;

  const handleAddText = (text: string, style: any) => {
    const newElement: DesignElement = {
      id: `text-${Date.now()}`,
      type: 'text',
      content: text,
      x: 35,
      y: 45,
      width: 30,
      height: 10,
      rotation: 0,
      fontSize: style.fontSize || 32,
      fontFamily: style.fontFamily || 'inherit',
      color: style.color || '#000000',
      fontWeight: style.fontWeight || 'bold',
      letterSpacing: style.letterSpacing || 'normal',
      textTransform: style.textTransform || 'uppercase',
      zIndex: elements.length,
    };
    setElements([...elements, newElement]);
    setSelectedElementId(newElement.id);
  };

  const handleAddImage = (imageUrl: string) => {
    const newElement: DesignElement = {
      id: `image-${Date.now()}`,
      type: 'image',
      content: imageUrl,
      x: 35,
      y: 40,
      width: 30,
      height: 30,
      rotation: 0,
      zIndex: elements.length,
    };
    setElements([...elements, newElement]);
    setSelectedElementId(newElement.id);
  };

  const handleElementUpdate = (id: string, updates: Partial<DesignElement>) => {
    setElements(elements.map((el) => (el.id === id ? { ...el, ...updates } : el)));
  };

  const handleDeleteElement = () => {
    if (selectedElementId) {
      setElements(elements.filter((el) => el.id !== selectedElementId));
      setSelectedElementId(null);
    }
  };

  const handleLayerChange = (direction: 'forward' | 'backward') => {
    if (!selectedElementId) return;
    
    const currentIndex = elements.findIndex((el) => el.id === selectedElementId);
    if (currentIndex === -1) return;

    const newElements = [...elements];
    if (direction === 'forward' && currentIndex < elements.length - 1) {
      [newElements[currentIndex], newElements[currentIndex + 1]] = [newElements[currentIndex + 1], newElements[currentIndex]];
    } else if (direction === 'backward' && currentIndex > 0) {
      [newElements[currentIndex], newElements[currentIndex - 1]] = [newElements[currentIndex - 1], newElements[currentIndex]];
    }

    // Update zIndex
    newElements.forEach((el, idx) => {
      el.zIndex = idx;
    });

    setElements(newElements);
  };

  const selectedElement = elements.find((el) => el.id === selectedElementId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="text-2xl font-black text-gray-900 hover:text-red-900 transition-colors">
              GAMEDAY
            </a>
            <span className="text-gray-300">|</span>
            <h1 className="text-lg font-bold text-gray-700">Design Studio</h1>
          </div>
          <nav className="flex items-center gap-6">
            <a href="/" className="text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors">
              Home
            </a>
            <a href="/" className="text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors">
              Shop All
            </a>
          </nav>
        </div>
      </header>

      {/* Main Studio */}
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-8">
          {/* Left: Canvas Area */}
          <div className="flex flex-col items-center justify-start">
            <DesignCanvas
              shirtColor={selectedColor.name}
              shirtImage={selectedColor.image}
              elements={elements}
              selectedElementId={selectedElementId}
              onElementUpdate={handleElementUpdate}
              onElementSelect={setSelectedElementId}
              printLocation={printLocation}
            />

            {/* Print Location Selector */}
            <div className="mt-6 bg-white rounded-xl p-4 shadow-lg border border-gray-200 w-full max-w-[420px]">
              <h3 className="text-sm font-bold text-gray-700 mb-3">Print Location</h3>
              <div className="grid grid-cols-2 gap-2">
                {printLocations.map((location) => (
                  <button
                    key={location}
                    onClick={() => setPrintLocation(location)}
                    className={`px-4 py-2.5 rounded-lg text-sm font-bold transition-all ${
                      printLocation === location
                        ? 'bg-red-900 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {location}
                    {PRINT_LOCATION_PRICES[location] > 0 && (
                      <span className="ml-1 text-xs opacity-75">
                        +${PRINT_LOCATION_PRICES[location].toFixed(2)}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Layer Controls */}
            {selectedElement && (
              <div className="mt-4 bg-white rounded-xl p-4 shadow-lg border border-gray-200 w-full max-w-[420px]">
                <h3 className="text-sm font-bold text-gray-700 mb-3">Layer Controls</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleLayerChange('forward')}
                    className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-bold transition-colors flex items-center justify-center gap-2"
                  >
                    <i className="ri-bring-forward text-base"></i>
                    Bring Forward
                  </button>
                  <button
                    onClick={() => handleLayerChange('backward')}
                    className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-bold transition-colors flex items-center justify-center gap-2"
                  >
                    <i className="ri-send-backward text-base"></i>
                    Send Backward
                  </button>
                  <button
                    onClick={handleDeleteElement}
                    className="px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm font-bold transition-colors flex items-center justify-center"
                  >
                    <i className="ri-delete-bin-line text-base"></i>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right: Control Panel */}
          <div className="flex flex-col gap-6">
            <DesignToolbar
              selectedColor={selectedColor}
              shirtColors={shirtColors}
              onColorChange={setSelectedColor}
              onAddText={handleAddText}
              onAddImage={handleAddImage}
              selectedElement={selectedElement}
              onElementUpdate={handleElementUpdate}
              selectedSize={selectedSize}
              onSizeChange={setSelectedSize}
              printLocation={printLocation}
            />

            {/* Sticky Cart Section with Price Breakdown */}
            <div className="bg-white rounded-xl p-6 shadow-xl border border-gray-200 sticky top-24">
              <div className="space-y-4">
                {/* Price Breakdown */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 font-medium">Base Price</span>
                    <span className="text-gray-900 font-bold">${BASE_PRICE.toFixed(2)}</span>
                  </div>
                  
                  {PRINT_LOCATION_PRICES[printLocation] > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 font-medium flex items-center gap-1">
                        {printLocation}
                        <i className="ri-information-line text-xs text-gray-400" title="Print location fee"></i>
                      </span>
                      <span className="text-gray-900 font-bold">+${PRINT_LOCATION_PRICES[printLocation].toFixed(2)}</span>
                    </div>
                  )}
                  
                  {elements.length > 1 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 font-medium flex items-center gap-1">
                        Design Complexity
                        <i className="ri-information-line text-xs text-gray-400" title={`${elements.length - 1} additional element(s)`}></i>
                      </span>
                      <span className="text-gray-900 font-bold">
                        +${((elements.length - 1) * DESIGN_COMPLEXITY_PRICE).toFixed(2)}
                      </span>
                    </div>
                  )}
                  
                  <div className="pt-2 border-t border-gray-300 flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-700">Unit Price</span>
                    <span className="text-lg font-black text-red-900">${unitPrice.toFixed(2)}</span>
                  </div>
                </div>

                {/* Quantity Selector */}
                <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                  <span className="text-sm font-semibold text-gray-600">Quantity</span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-bold transition-colors"
                    >
                      −
                    </button>
                    <span className="text-lg font-bold text-gray-900 w-8 text-center">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-bold transition-colors"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Total Price */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-lg font-bold text-gray-900 block">Total</span>
                    {quantity > 1 && (
                      <span className="text-xs text-gray-500">{quantity} × ${unitPrice.toFixed(2)}</span>
                    )}
                  </div>
                  <span className="text-2xl font-black text-red-900">${totalPrice.toFixed(2)}</span>
                </div>

                {/* Add to Cart Button */}
                <button
                  onClick={() => setShowOrderForm(true)}
                  className="w-full bg-red-900 hover:bg-red-800 text-white font-bold py-4 rounded-xl transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-shopping-cart-line text-xl"></i>
                  Add to Cart
                </button>

                {/* Shipping Info */}
                <div className="pt-4 border-t border-gray-200 space-y-2">
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <i className="ri-truck-line text-sm"></i>
                    <span>Free shipping on orders over $50</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <i className="ri-time-line text-sm"></i>
                    <span>Ships in 3-5 business days</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <i className="ri-shield-check-line text-sm"></i>
                    <span>100% satisfaction guarantee</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Order Form Modal */}
      {showOrderForm && (
        <OrderForm
          customization={{
            shirtColor: selectedColor.name,
            printLocation,
            elements: elements.map((el) => ({
              type: el.type,
              content: el.content,
              style: {
                fontSize: el.fontSize,
                fontFamily: el.fontFamily,
                color: el.color,
                fontWeight: el.fontWeight,
              },
            })),
            size: selectedSize,
            quantity,
          }}
          totalPrice={totalPrice}
          onClose={() => setShowOrderForm(false)}
        />
      )}
    </div>
  );
}